import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Paper,
  IconButton,
  Chip,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SendIcon from '@material-ui/icons/Send';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import HistoryIcon from '@material-ui/icons/History';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import { useApi, discoveryApiRef } from '@backstage/core-plugin-api';
import { EmptyState } from '@backstage/core-components';
import { githubAuthApiRef } from '../../apis';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  personaSelector: {
    minWidth: 200,
  },
  modelSelector: {
    minWidth: 140,
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  message: {
    marginBottom: theme.spacing(2),
    maxWidth: '85%',
  },
  userMessage: {
    marginLeft: 'auto',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  assistantMessage: {
    marginRight: 'auto',
    backgroundColor: theme.palette.background.paper,
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1),
  },
  inputContainer: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  inputBox: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  contextChip: {
    margin: theme.spacing(0.5),
  },
  toolChip: {
    margin: theme.spacing(0.25),
    fontSize: '0.7rem',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    fontSize: '0.75rem',
  },
  statusBadgeOff: {
    backgroundColor: theme.palette.grey[500],
  },
  streamingDot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    animation: '$pulse 1.4s ease-in-out infinite',
    marginLeft: theme.spacing(0.5),
  },
  '@keyframes pulse': {
    '0%': { opacity: 0.3 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0.3 },
  },
  changesetCard: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `2px solid ${theme.palette.primary.main}`,
  },
  changesetHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  fileChange: {
    padding: theme.spacing(1),
    margin: theme.spacing(0.5, 0),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '0.85rem',
  },
  actionCreate: {
    color: theme.palette.success.main,
    fontWeight: 'bold',
  },
  actionUpdate: {
    color: theme.palette.warning.main,
    fontWeight: 'bold',
  },
  actionDelete: {
    color: theme.palette.error.main,
    fontWeight: 'bold',
  },
  deployTimeline: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  timelineStep: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    fontSize: '0.85rem',
  },
  stepDone: {
    color: theme.palette.success.main,
  },
  stepActive: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  stepPending: {
    color: theme.palette.text.disabled,
  },
  historyDrawer: {
    width: 320,
    padding: theme.spacing(2),
  },
  historyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  historyItem: {
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(0.5),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  historyItemActive: {
    backgroundColor: theme.palette.action.selected,
  },
  historyTimestamp: {
    fontSize: '0.7rem',
    color: theme.palette.text.disabled,
  },
  suggestedPromptsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
  suggestedPromptChip: {
    cursor: 'pointer',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

// ─── Types ───────────────────────────────────────────────────────────────────

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  toolCalls?: string[];
}

interface ChatSession {
  id: string;
  title: string;
  persona_id: string | null;
  created_at: string;
  updated_at: string;
}

interface FileChange {
  path: string;
  action: 'create' | 'update' | 'delete';
  content: string;
}

interface PendingChangeset {
  requestId: string;
  changes: FileChange[];
  summary: string;
}

type DeployStage = 'idle' | 'committing' | 'committed' | 'building' | 'deploying' | 'live' | 'error';

interface Persona {
  id: string;
  name: string;
  description: string;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface CopilotChatInterfaceProps {
  /** Fully-qualified entity ref, e.g. "component:default/my-app". Undefined = global mode. */
  entityRef?: string;
  /** Short label shown in the context chip, e.g. "Component/my-app". */
  entityLabel?: string;
  /** Suggested prompt chips shown when the conversation is empty. */
  suggestedPrompts?: string[];
}

// ─── Component ───────────────────────────────────────────────────────────────

export const CopilotChatInterface = ({
  entityRef,
  entityLabel,
  suggestedPrompts,
}: CopilotChatInterfaceProps) => {
  const classes = useStyles();
  const discovery = useApi(discoveryApiRef);
  const githubAuth = useApi(githubAuthApiRef);

  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<string>('');
  const [models, setModels] = useState<string[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>(
    () => localStorage.getItem('copilot.model') ?? 'claude-sonnet-4-6',
  );
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeTools, setActiveTools] = useState<string[]>([]);
  const [healthStatus, setHealthStatus] = useState<'checking' | 'ok' | 'error' | 'disabled'>('checking');
  const [pendingChangeset, setPendingChangeset] = useState<PendingChangeset | null>(null);
  const [deployStage, setDeployStage] = useState<DeployStage>('idle');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<Message[]>([]);

  // History (only in entity mode)
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const currentSessionIdRef = useRef<string | null>(null);
  const ghTokenRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    checkHealth();
    loadPersonas();
  }, []);

  const checkHealth = useCallback(async () => {
    try {
      const baseUrl = await discovery.getBaseUrl('copilot');
      const response = await fetch(`${baseUrl}/health`);
      const data = await response.json();
      if (data.status === 'disabled') {
        setHealthStatus('disabled');
      } else {
        setHealthStatus(data.status === 'ok' ? 'ok' : 'error');
      }
    } catch {
      setHealthStatus('error');
    }
  }, [discovery]);

  const loadPersonas = useCallback(async () => {
    try {
      const baseUrl = await discovery.getBaseUrl('copilot');
      const response = await fetch(`${baseUrl}/personas`);
      const data = await response.json();
      setPersonas(data.personas || []);
      if (data.personas && data.personas.length > 0) {
        setSelectedPersona(data.personas[0].id);
      }
    } catch (error) {
      console.error('Failed to load personas:', error);
    }
  }, [discovery]);

  const loadModels = useCallback(async (token: string) => {
    try {
      const baseUrl = await discovery.getBaseUrl('copilot');
      const response = await fetch(`${baseUrl}/models?githubToken=${encodeURIComponent(token)}`);
      if (response.ok) {
        const data = await response.json();
        const ids: string[] = (data.models ?? []).map((m: any) => m.id ?? m);
        if (ids.length > 0) {
          setModels(ids);
          setSelectedModel(prev => ids.includes(prev) ? prev : ids[0]);
        }
      }
    } catch {
      // non-fatal
    }
  }, [discovery]);

  const loadHistory = useCallback(async (token?: string) => {
    try {
      const baseUrl = await discovery.getBaseUrl('copilot');
      // In global mode use 'global' as the synthetic entityRef key
      const params = new URLSearchParams({ entityRef: entityRef ?? 'global' });
      if (token) params.set('githubToken', token);
      const response = await fetch(`${baseUrl}/history?${params}`);
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions ?? []);
      }
    } catch {
      // silent
    }
  }, [discovery, entityRef]);

  const saveSession = useCallback(async (
    msgs: Message[],
    token?: string,
    sessionId?: string | null,
    personaId?: string,
  ): Promise<string | null> => {
    if (msgs.length === 0) return null;
    try {
      const baseUrl = await discovery.getBaseUrl('copilot');
      const response = await fetch(`${baseUrl}/history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId ?? undefined,
          entityRef: entityRef ?? 'global',
          personaId,
          messages: msgs.map(m => ({
            role: m.role,
            content: m.content,
            timestamp: m.timestamp.toISOString(),
            toolCalls: m.toolCalls,
          })),
          githubToken: token,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        return data.id as string;
      }
    } catch {
      // silent
    }
    return null;
  }, [discovery, entityRef]);

  const loadSession = useCallback(async (sessionId: string) => {
    const token = ghTokenRef.current;
    try {
      const baseUrl = await discovery.getBaseUrl('copilot');
      const params = new URLSearchParams();
      if (token) params.set('githubToken', token);
      const response = await fetch(`${baseUrl}/history/${sessionId}?${params}`);
      if (!response.ok) return;
      const session = await response.json();
      const restoredMessages: Message[] = (session.messages ?? []).map((m: any) => ({
        ...m,
        timestamp: new Date(m.timestamp ?? Date.now()),
      }));
      setMessages(restoredMessages);
      setCurrentSessionId(session.id);
      currentSessionIdRef.current = session.id;
      if (session.persona_id) setSelectedPersona(session.persona_id);
      setHistoryOpen(false);
    } catch {
      // ignore
    }
  }, [discovery]);

  const deleteSession = useCallback(async (sessionId: string) => {
    const token = ghTokenRef.current;
    try {
      const baseUrl = await discovery.getBaseUrl('copilot');
      const params = new URLSearchParams();
      if (token) params.set('githubToken', token);
      await fetch(`${baseUrl}/history/${sessionId}?${params}`, { method: 'DELETE' });
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (currentSessionIdRef.current === sessionId) {
        setCurrentSessionId(null);
        currentSessionIdRef.current = null;
        setMessages([]);
        messagesRef.current = [];
      }
    } catch {
      // ignore
    }
  }, [discovery]);

  const startNewChat = () => {
    setMessages([]);
    messagesRef.current = [];
    setCurrentSessionId(null);
    currentSessionIdRef.current = null;
    setPendingChangeset(null);
    setDeployStage('idle');
    setHistoryOpen(false);
  };

  const sendMessage = async (overrideInput?: string) => {
    const text = overrideInput ?? input;
    if (!text.trim() || isStreaming) return;

    const userMessage: Message = {
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsStreaming(true);
    setActiveTools([]);

    try {
      const baseUrl = await discovery.getBaseUrl('copilot');
      const ghToken = await githubAuth.getAccessToken(['repo']);
      ghTokenRef.current = ghToken;
      if (sessions.length === 0) loadHistory(ghToken);
      if (models.length === 0) loadModels(ghToken);

      const response = await fetch(`${baseUrl}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          personaId: selectedPersona || undefined,
          entityRef: entityRef || undefined,
          githubToken: ghToken,
          model: selectedModel,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to start copilot chat');
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';
      const tools: string[] = [];

      const placeholderIdx = messages.length + 1;
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: '', timestamp: new Date(), toolCalls: [] },
      ]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === 'assistant.message_delta') {
              accumulated += event.content;
              setMessages((prev) => {
                const updated = [...prev];
                updated[placeholderIdx] = {
                  ...updated[placeholderIdx],
                  content: accumulated,
                  toolCalls: [...tools],
                };
                return updated;
              });
            } else if (event.type === 'tool.execution_start') {
              tools.push(event.tool);
              setActiveTools([...tools]);
            } else if (event.type === 'tool.execution_complete') {
              setActiveTools((prev) => prev.filter((t) => t !== event.tool));
            } else if (event.type === 'done') {
              const finalContent = accumulated || event.content;
              setMessages((prev) => {
                const updated = [...prev];
                updated[placeholderIdx] = {
                  ...updated[placeholderIdx],
                  content: finalContent,
                  toolCalls: [...tools],
                };
                messagesRef.current = updated;
                return updated;
              });
            } else if (event.type === 'changeset') {
              setPendingChangeset({
                requestId: event.requestId,
                changes: event.changes ?? [],
                summary: event.summary ?? '',
              });
            } else if (event.type === 'permission_request') {
              tools.push(`⚡ ${event.tool}`);
              setActiveTools([...tools]);
            } else if (event.type === 'error') {
              setMessages((prev) => {
                const updated = [...prev];
                updated[placeholderIdx] = {
                  ...updated[placeholderIdx],
                  content: `Error: ${event.error}`,
                };
                messagesRef.current = updated;
                return updated;
              });
            }
          } catch {
            // ignore malformed SSE lines
          }
        }
      }
    } catch (error) {
      console.error('Copilot chat error:', error);
      setMessages((prev) => {
        const next: Message[] = [
          ...prev.slice(0, -1),
          {
            role: 'assistant' as const,
            content: 'Sorry, I encountered an error connecting to Copilot.',
            timestamp: new Date(),
          },
        ];
        messagesRef.current = next;
        return next;
      });
    } finally {
      setIsStreaming(false);
      setActiveTools([]);
      const finalMessages = messagesRef.current;
      const savedSessionId = currentSessionIdRef.current;
      if (finalMessages.length > 0) {
        saveSession(finalMessages, ghTokenRef.current, savedSessionId, selectedPersona).then(newId => {
          if (newId && !savedSessionId) {
            setCurrentSessionId(newId);
            currentSessionIdRef.current = newId;
            loadHistory(ghTokenRef.current);
          }
        });
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleApplyChangeset = async () => {
    if (!pendingChangeset) return;
    setDeployStage('committing');

    try {
      const baseUrl = await discovery.getBaseUrl('copilot');
      const response = await fetch(`${baseUrl}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ requestId: pendingChangeset.requestId }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error ?? 'Apply failed');
      }

      const result = await response.json();
      setDeployStage('committed');

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `Changes committed to ${result.owner}/${result.repo} (${result.branch}):\n${result.files.map((f: any) => `  ${f.status}: ${f.path}`).join('\n')}\n\nCI pipeline will trigger automatically.`,
          timestamp: new Date(),
        },
      ]);

      setPendingChangeset(null);
      setTimeout(() => setDeployStage('building'), 2000);
      setTimeout(() => setDeployStage('deploying'), 8000);
      setTimeout(() => setDeployStage('live'), 15000);
    } catch (error: any) {
      console.error('Apply changeset error:', error);
      setDeployStage('error');
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `Failed to apply changes: ${error.message}`,
          timestamp: new Date(),
        },
      ]);
    }
  };

  const DEPLOY_STEPS: Array<{ key: DeployStage; label: string }> = [
    { key: 'committing', label: 'Committing to Git' },
    { key: 'committed', label: 'Changes committed' },
    { key: 'building', label: 'CI pipeline running' },
    { key: 'deploying', label: 'ArgoCD deploying' },
    { key: 'live', label: 'Service live' },
  ];

  const getStepClass = (stepKey: DeployStage) => {
    const order = ['committing', 'committed', 'building', 'deploying', 'live'];
    const currentIdx = order.indexOf(deployStage);
    const stepIdx = order.indexOf(stepKey);
    if (stepIdx < currentIdx) return classes.stepDone;
    if (stepIdx === currentIdx) return classes.stepActive;
    return classes.stepPending;
  };

  const getStepIcon = (stepKey: DeployStage) => {
    const order = ['committing', 'committed', 'building', 'deploying', 'live'];
    const currentIdx = order.indexOf(deployStage);
    const stepIdx = order.indexOf(stepKey);
    if (stepIdx < currentIdx) return '✓';
    if (stepIdx === currentIdx) return '●';
    return '○';
  };

  if (healthStatus === 'disabled') {
    return (
      <EmptyState
        title="GitHub Copilot not enabled"
        missing="info"
        description="Enable Copilot by setting copilot.enabled=true and configuring copilot.serverUrl in app-config."
      />
    );
  }

  if (healthStatus === 'error') {
    return (
      <EmptyState
        title="Copilot server unavailable"
        missing="info"
        description="The Copilot CLI server is not reachable. Check that the copilot-server deployment is running."
      />
    );
  }

  const selectedPersonaInfo = personas.find((p) => p.id === selectedPersona);
  const isGlobalMode = !entityRef;

  return (
    <Card className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h6">GitHub Copilot</Typography>

        <span
          className={`${classes.statusBadge} ${
            healthStatus !== 'ok' ? classes.statusBadgeOff : ''
          }`}
        >
          {healthStatus === 'checking' ? (
            <CircularProgress size={12} color="inherit" />
          ) : (
            '●'
          )}
          {healthStatus === 'ok' ? 'Connected' : 'Checking...'}
        </span>

        <FormControl className={classes.personaSelector} size="small">
          <InputLabel>Persona</InputLabel>
          <Select
            value={selectedPersona}
            onChange={(e) => setSelectedPersona(e.target.value as string)}
          >
            {personas.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className={classes.modelSelector} size="small">
          <InputLabel>Model</InputLabel>
          <Select
            value={selectedModel}
            onChange={(e) => {
              const m = e.target.value as string;
              setSelectedModel(m);
              localStorage.setItem('copilot.model', m);
            }}
          >
            {(models.length > 0
              ? models
              : ['claude-sonnet-4-6', 'claude-sonnet-4-5', 'gpt-5.4', 'gpt-4.1', 'gpt-4o', 'gemini-2.5-pro']
            ).map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedPersonaInfo && (
          <Typography variant="body2" color="textSecondary">
            {selectedPersonaInfo.description}
          </Typography>
        )}

        {entityLabel && (
          <Chip
            label={entityLabel}
            size="small"
            className={classes.contextChip}
          />
        )}

        {/* History button — entity mode only */}
        <Box ml="auto">
            <Tooltip title="Chat history">
              <IconButton size="small" onClick={() => {
                loadHistory(ghTokenRef.current);
                setHistoryOpen(true);
              }}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
      </Box>

      <Box className={classes.messagesContainer}>
        {messages.length === 0 && (
          <Box textAlign="center" mt={4}>
            <EmojiObjectsIcon style={{ fontSize: 48, opacity: 0.3 }} />
            <Typography variant="body2" color="textSecondary">
              {isGlobalMode
                ? 'Your AI platform assistant — ask about components, APIs, onboarding, or platform capabilities.'
                : 'Ask Copilot about this component — architecture, code, deployments, or troubleshooting.'}
            </Typography>
            {isGlobalMode && suggestedPrompts && suggestedPrompts.length > 0 && (
              <Box className={classes.suggestedPromptsGrid}>
                {suggestedPrompts.map((prompt) => (
                  <Chip
                    key={prompt}
                    label={prompt}
                    className={classes.suggestedPromptChip}
                    onClick={() => sendMessage(prompt)}
                    clickable
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
        {messages.map((message, index) => (
          <Paper
            key={index}
            className={`${classes.message} ${
              message.role === 'user' ? classes.userMessage : classes.assistantMessage
            }`}
            elevation={2}
          >
            <CardContent>
              <Box className={classes.messageHeader}>
                {message.role === 'user' ? (
                  <AccountCircleIcon fontSize="small" />
                ) : (
                  <EmojiObjectsIcon fontSize="small" />
                )}
                <Typography variant="caption">
                  {message.role === 'user'
                    ? 'You'
                    : selectedPersonaInfo?.name || 'Copilot'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {message.timestamp.toLocaleTimeString()}
                </Typography>
              </Box>
              {message.toolCalls && message.toolCalls.length > 0 && (
                <Box mb={0.5}>
                  {message.toolCalls.map((tool, i) => (
                    <Chip
                      key={i}
                      label={tool}
                      size="small"
                      variant="outlined"
                      className={classes.toolChip}
                    />
                  ))}
                </Box>
              )}
              <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                {message.content}
                {isStreaming &&
                  index === messages.length - 1 &&
                  message.role === 'assistant' && (
                    <span className={classes.streamingDot} />
                  )}
              </Typography>
            </CardContent>
          </Paper>
        ))}
        {activeTools.length > 0 && (
          <Box display="flex" alignItems="center" mb={1}>
            <CircularProgress size={14} style={{ marginRight: 8 }} />
            <Typography variant="caption" color="textSecondary">
              Running: {activeTools.join(', ')}
            </Typography>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Pending changeset — Approve & Deploy card */}
      {pendingChangeset && (
        <Card className={classes.changesetCard} variant="outlined">
          <CardContent>
            <Box className={classes.changesetHeader}>
              <Typography variant="subtitle1">Proposed Changes</Typography>
              <Box>
                <IconButton size="small" onClick={() => setPendingChangeset(null)} title="Dismiss">
                  ✕
                </IconButton>
              </Box>
            </Box>
            {pendingChangeset.summary && (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {pendingChangeset.summary}
              </Typography>
            )}
            <Box mt={1} mb={2}>
              {pendingChangeset.changes.map((file, i) => (
                <Box key={i} className={classes.fileChange}>
                  <span
                    className={
                      file.action === 'create'
                        ? classes.actionCreate
                        : file.action === 'delete'
                          ? classes.actionDelete
                          : classes.actionUpdate
                    }
                  >
                    {file.action.toUpperCase()}
                  </span>{' '}
                  {file.path}
                </Box>
              ))}
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApplyChangeset}
              disabled={deployStage !== 'idle'}
            >
              {deployStage === 'idle' ? 'Approve & Deploy' : 'Applying...'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Deploy progress timeline */}
      {deployStage !== 'idle' && (
        <Box className={classes.deployTimeline}>
          <Typography variant="subtitle2" gutterBottom>Deploy Progress</Typography>
          {DEPLOY_STEPS.map((step) => (
            <Box key={step.key} className={`${classes.timelineStep} ${getStepClass(step.key)}`}>
              <span>{getStepIcon(step.key)}</span>
              <span>{step.label}</span>
              {step.key === deployStage && deployStage !== 'live' && deployStage !== 'error' && (
                <CircularProgress size={12} />
              )}
            </Box>
          ))}
          {deployStage === 'error' && (
            <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
              Deployment failed — check the messages above for details.
            </Typography>
          )}
          {deployStage === 'live' && (
            <Typography variant="body2" color="primary" style={{ marginTop: 8 }}>
              Service is live and healthy.
            </Typography>
          )}
        </Box>
      )}

      <Box className={classes.inputContainer}>
        <Box className={classes.inputBox}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Ask GitHub Copilot..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isStreaming || healthStatus !== 'ok'}
            variant="outlined"
            size="small"
          />
          <IconButton
            color="primary"
            onClick={() => sendMessage()}
            disabled={isStreaming || !input.trim() || healthStatus !== 'ok'}
          >
            {isStreaming ? <CircularProgress size={24} /> : <SendIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* History drawer */}
      <Drawer anchor="right" open={historyOpen} onClose={() => setHistoryOpen(false)}>
          <Box className={classes.historyDrawer}>
            <Box className={classes.historyHeader}>
              <Typography variant="subtitle1">Chat history</Typography>
              <Tooltip title="New chat">
                <IconButton size="small" onClick={startNewChat}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Divider />
            {sessions.length === 0 && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                No saved sessions yet.
              </Typography>
            )}
            <List disablePadding>
              {sessions.map((session) => (
                <ListItem
                  key={session.id}
                  className={`${classes.historyItem} ${session.id === currentSessionId ? classes.historyItemActive : ''}`}
                  onClick={() => loadSession(session.id)}
                  dense
                >
                  <ListItemText
                    primary={session.title}
                    secondary={
                      <span className={classes.historyTimestamp}>
                        {new Date(session.updated_at).toLocaleString()}
                        {session.persona_id ? ` · ${session.persona_id}` : ''}
                      </span>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => { e.stopPropagation(); deleteSession(session.id); }}
                      title="Delete session"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>
    </Card>
  );
};
